import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Trash2,
  PencilLine,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-hot-toast";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const ITEMS_PER_PAGE = 5;

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState({
    question: "",
    answer: "",
    isVerified: true,
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchFAQs = async () => {
    try {
      const { data } = await axios.get(`${SERVER_URL}/api/faq`);
      setFaqs(data);
    } catch {
      toast.error("We're having trouble loading FAQs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${SERVER_URL}/api/faq/${editId}`, form);
        toast.success("FAQ updated");
      } else {
        await axios.post(`${SERVER_URL}/api/faq`, form);
        toast.success("FAQ added");
      }
      resetForm();
      fetchFAQs();
    } catch {
      toast.error("Couldn't save FAQ. Please try again.");
    }
  };

  const handleEdit = (faq) => {
    setForm(faq);
    setEditId(faq._id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm({ question: "", answer: "", isVerified: true });
    setEditId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${SERVER_URL}/api/faq/${id}`);
      fetchFAQs();
      toast.success("FAQ deleted");
    } catch {
      toast.error("Couldn't delete FAQ. Please try again.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected FAQ(s)?`))
      return;
    try {
      await Promise.all(
        selectedIds.map((id) => axios.delete(`${SERVER_URL}/api/faq/${id}`)),
      );
      fetchFAQs();
      setSelectedIds([]);
      toast.success("Bulk delete successful");
    } catch {
      toast.error("Couldn't delete selected FAQs. Please try again.");
    }
  };

  const filteredFaqs = faqs.filter((faq) => {
    const matchesQuery = faq.question
      .toLowerCase()
      .includes(query.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "verified" && faq.isVerified) ||
      (filter === "unverified" && !faq.isVerified);
    return matchesQuery && matchesFilter;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredFaqs.length / ITEMS_PER_PAGE),
  );
  const paginatedFaqs = filteredFaqs.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
          Admin FAQs
        </h1>
        <p className="text-gray-500 mt-1">
          Create and manage frequently asked questions
        </p>
      </div>

      {/* Form */}
      <div className="bg-white border border-gray-100 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <span className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center text-white text-sm">
            {editId ? "✏" : "+"}
          </span>
          {editId ? "Edit FAQ" : "Add New FAQ"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Question
            </label>
            <input
              type="text"
              name="question"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              placeholder="Enter the question..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Answer
            </label>
            <textarea
              name="answer"
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              placeholder="Enter the answer..."
              rows={4}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition resize-none"
              required
            />
          </div>

          <label className="inline-flex items-center gap-3 cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={form.isVerified}
                onChange={(e) =>
                  setForm({ ...form, isVerified: e.target.checked })
                }
                className="sr-only"
              />
              <div
                className={`w-12 h-6 rounded-full transition-colors duration-300 ${form.isVerified ? "bg-yellow-500" : "bg-gray-200"}`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300 mt-0.5 ${form.isVerified ? "translate-x-6 ml-0.5" : "translate-x-0.5"}`}
                />
              </div>
            </div>
            <span
              className={`font-medium text-sm ${form.isVerified ? "text-yellow-600" : "text-gray-500"}`}
            >
              {form.isVerified ? "Verified" : "Mark as Verified"}
            </span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:from-yellow-600 hover:to-yellow-700 shadow-lg shadow-yellow-500/20 transition flex items-center gap-2"
            >
              <PlusCircle size={16} />
              {editId ? "Update FAQ" : "Add FAQ"}
            </button>
            {editId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Search + Filter Controls */}
      <div className="flex flex-col md:flex-row gap-3">
        <input
          type="text"
          placeholder="Search FAQs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition shadow-sm"
        />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition shadow-sm"
        >
          <option value="all">All FAQs</option>
          <option value="verified">Verified</option>
          <option value="unverified">Not Verified</option>
        </select>
      </div>

      {/* Bulk Delete */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded-2xl px-6 py-3">
          <span className="text-red-700 font-semibold text-sm">
            {selectedIds.length} selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition text-sm font-semibold"
          >
            <Trash2 size={14} /> Delete Selected
          </button>
        </div>
      )}

      {/* FAQ List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <CircularProgress size={35} sx={{ color: "#eab308" }} />
        </div>
      ) : paginatedFaqs.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-[2rem] flex flex-col items-center justify-center h-64 gap-3">
          <HelpCircle className="w-12 h-12 text-gray-200" />
          <p className="text-gray-400 font-bold text-sm tracking-tight">
            No FAQs cataloged
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {paginatedFaqs.map((faq) => (
            <div
              key={faq._id}
              className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 group ${
                selectedIds.includes(faq._id)
                  ? "border-yellow-300 bg-yellow-50/30"
                  : "border-gray-100"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="mt-1 flex flex-col items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(faq._id)}
                      onChange={() => toggleSelect(faq._id)}
                      className="w-4 h-4 accent-yellow-500 cursor-pointer"
                    />
                    <span className="text-[9px] font-black text-gray-300 uppercase tracking-tighter">
                      Select
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-950 rounded-lg flex items-center justify-center text-yellow-500 text-[10px] font-black group-hover:from-yellow-500 group-hover:to-yellow-600 group-hover:text-white transition-all duration-500">
                        Q
                      </div>
                      <div className="flex flex-col truncate">
                        <h3 className="font-black text-gray-900 text-sm uppercase tracking-tight truncate leading-none">
                          {faq.question}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest mt-1.5 ${faq.isVerified ? "text-green-600" : "text-red-500"}`}
                        >
                          {faq.isVerified ? "Public Display" : "Private Draft"}
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mt-3">
                      <p className="text-gray-600 text-[13px] leading-relaxed font-medium italic">
                        "{faq.answer}"
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(faq)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-all"
                    title="Modify"
                  >
                    <PencilLine size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(faq._id)}
                    className="p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 pb-4">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
          >
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-semibold text-gray-700 bg-white border border-gray-100 px-4 py-2 rounded-xl">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFAQs;
